import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Grid,
  Select,
  MenuItem,
  IconButton,
  TextField,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CodeIcon from '@mui/icons-material/Code';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import MapComponent from '../Map/MapComponent';
import { User, Location, UserPrivacy } from '../../types/interfaces';
import PrivacyOverview from './PrivacyOverview';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-hot-toast';
import { useGetIdentityOptionsQuery, useUpdateUserMutation, useDeleteUserMutation } from '../../redux/adminApiSlice';

interface UserCardProps {
  user: User;
  locations: Location[];
  onUserUpdate?: (updatedUser: User | null) => void;
  onUserDeleted: (userId: string) => void;
}

const getProfilePicture = (url?: string) => {
  if (!url) return undefined;
  if (url.startsWith('http')) {
    return url;
  }
  if (url.startsWith('file://')) {
    return undefined;
  }
  return `http://localhost:3000/${url}`;
};

interface EditableInfoRowProps {
  label: string;
  value: string | undefined;
  isEditing: boolean;
  onEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
  options?: string[];
  isPrivate?: boolean;
  defaultValue?: string;
  type?: string;
}

const EditableInfoRow = ({ 
  label, 
  value, 
  isEditing, 
  onEdit, 
  onSave, 
  onCancel, 
  options,
  isPrivate,
  defaultValue,
  type
}: EditableInfoRowProps) => {
  const [editValue, setEditValue] = useState(defaultValue || value || '');

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      py: 0.5,
      '&:hover .edit-button': {
        opacity: 1,
      },
    }}>
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          minWidth: 120,
          fontWeight: 500,
        }}
      >
        {label}:
      </Typography>
      {isEditing ? (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            {options ? (
              <Select
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
              >
                {options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                size="small"
                type={type || "text"}
                InputLabelProps={type === "date" ? { shrink: true } : undefined}
              />
            )}
          </FormControl>
          <IconButton 
            size="small" 
            onClick={() => onSave(editValue)}
            color="primary"
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={onCancel}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <Typography variant="body2" sx={{ flex: 1 }}>
            {value} {isPrivate && <Typography component="span" color="text.secondary">(Private)</Typography>}
          </Typography>
          <IconButton 
            size="small" 
            onClick={onEdit}
            className="edit-button"
            sx={{ opacity: 0, transition: 'opacity 0.2s' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

const UserCard: React.FC<UserCardProps> = ({ user, locations, onUserUpdate, onUserDeleted }) => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [jsonModalOpen, setJsonModalOpen] = useState(false);
  const [allowMultiplePromotes, setAllowMultiplePromotes] = useState(
    user.settings && 'allowMultiplePromotes' in user.settings ? user.settings.allowMultiplePromotes : false
  );
  const [uploadingPicture, setUploadingPicture] = useState<string | null>(null);

  const facePictureInputRef = useRef<HTMLInputElement>(null);
  const publicPictureInputRef = useRef<HTMLInputElement>(null);

  const { data: identityOptions } = useGetIdentityOptionsQuery();
  const [updateUser] = useUpdateUserMutation();
  const [deleteUser] = useDeleteUserMutation();

  const hasLocation = user.location?.coordinates && user.location.coordinates.length === 2;
  const mapCenter = hasLocation && user.location ? {
    lat: user.location.coordinates[1],
    lng: user.location.coordinates[0]
  } : null;

  const getLocationName = (locationRef: string | Location | undefined): string => {
    if (!locationRef) return 'Unknown';
    if (typeof locationRef === 'string') {
      const location = locations.find(loc => loc._id === locationRef);
      return location ? location.name : 'Unknown';
    }
    return locationRef.name;
  };

  const handleSave = async (field: string, value: string | boolean | Partial<User['settings']> | UserPrivacy) => {
    try {
      const updates: Partial<User> = {};
      
      if (field === 'settings') {
        updates.settings = value as User['settings'];
      } else if (field === 'privacy') {
        updates.privacy = value as UserPrivacy;
      } else if (typeof value === 'string' || typeof value === 'boolean') {
        (updates as any)[field] = value;
      }

      const response = await updateUser({
        id: user._id,
        updates
      });

      if ('data' in response) {
        if (onUserUpdate) {
          onUserUpdate(response.data || null);
        }
        toast.success(`User ${field} updated successfully`);
      } else if ('error' in response) {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      toast.error(`Failed to update user ${field}`);
      throw error; // Re-throw so calling code can handle it
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteUser(user._id);
      
      if ('data' in response && response.data) {
        // Show success message with number of deleted posts
        toast.success(`${response.data.message} (${response.data.deletedPostsCount} posts removed)`);
        onUserDeleted(user._id);
      } else if ('error' in response) {
        throw new Error('Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleSettingsUpdate = async (newValue: boolean) => {
    try {
      const updates: Partial<User> = {
        settings: {
          ...(user.settings || {}),
          allowMultiplePromotes: newValue
        }
      };

      const response = await updateUser({
        id: user._id,
        updates
      });

      if ('data' in response) {
        setAllowMultiplePromotes(newValue);
        if (onUserUpdate) {
          onUserUpdate(response.data || null);
        }
        toast.success(`Multiple promotes ${newValue ? 'enabled' : 'disabled'} for user ${user.username}`);
      } else {
        throw new Error('Failed to update user settings');
      }
    } catch (error) {
      // Revert the toggle if update fails
      setAllowMultiplePromotes(!newValue);
    }
  };

  const handleMultiplePromotesClick = () => {
    const newValue = !allowMultiplePromotes;
    handleSettingsUpdate(newValue);
  };

  useEffect(() => {
    setAllowMultiplePromotes(
      user.settings && 'allowMultiplePromotes' in user.settings ? user.settings.allowMultiplePromotes : false
    );
  }, [user.settings]);

  const handlePictureUpload = async (file: File, pictureType: 'facePicture' | 'publicPicture') => {
    try {
      setUploadingPicture(pictureType);
      
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to the backend
      const response = await fetch(`http://localhost:3000/api/users/admin/users/${user._id}/upload-${pictureType === 'facePicture' ? 'face' : 'public'}-picture`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Upload failed');
      }
      
      const result = await response.json();
      
      // Update the user with the new picture URL
      const updates: Partial<User> = {
        [pictureType]: result.url || result.filename
      };
      
      const updateResponse = await updateUser({
        id: user._id,
        updates
      });
      
      if ('data' in updateResponse) {
        if (onUserUpdate) {
          onUserUpdate(updateResponse.data || null);
        }
        toast.success(`${pictureType === 'facePicture' ? 'Face' : 'Public'} picture updated successfully`);
      } else {
        throw new Error('Failed to update user');
      }
    } catch (error) {
      toast.error(`Failed to upload ${pictureType === 'facePicture' ? 'face' : 'public'} picture`);
    } finally {
      setUploadingPicture(null);
    }
  };

  const handlePictureClick = (pictureType: 'facePicture' | 'publicPicture') => {
    const inputRef = pictureType === 'facePicture' ? facePictureInputRef : publicPictureInputRef;
    inputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, pictureType: 'facePicture' | 'publicPicture') => {
    const file = event.target.files?.[0];
    if (file) {
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      handlePictureUpload(file, pictureType);
    }
    
    // Reset the input
    event.target.value = '';
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mr: 2, gap: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover .upload-overlay': { opacity: 1 }
                    }}
                    onClick={() => handlePictureClick('facePicture')}
                  >
                    <Avatar 
                      src={getProfilePicture(user.facePicture)}
                      sx={{ 
                        width: 56, 
                        height: 56,
                        opacity: uploadingPicture === 'facePicture' ? 0.5 : 1
                      }}
                    />
                    <Box
                      className="upload-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        borderRadius: '50%'
                      }}
                    >
                      <PhotoCameraIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Face Picture
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box 
                    sx={{ 
                      position: 'relative',
                      cursor: 'pointer',
                      '&:hover .upload-overlay': { opacity: 1 }
                    }}
                    onClick={() => handlePictureClick('publicPicture')}
                  >
                    <Avatar 
                      src={getProfilePicture(user.publicPicture)}
                      sx={{ 
                        width: 56, 
                        height: 56,
                        opacity: uploadingPicture === 'publicPicture' ? 0.5 : 1
                      }}
                    />
                    <Box
                      className="upload-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        borderRadius: '50%'
                      }}
                    >
                      <PhotoCameraIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                    Public Picture
                  </Typography>
                </Box>
              </Box>
              
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={facePictureInputRef}
                onChange={(e) => handleFileChange(e, 'facePicture')}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <input
                type="file"
                ref={publicPictureInputRef}
                onChange={(e) => handleFileChange(e, 'publicPicture')}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <Box>
                <Typography variant="h6">{user.displayName}</Typography>
                <Typography variant="body2" color="text.secondary">@{user.username}</Typography>
                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <IconButton
                onClick={() => setJsonModalOpen(true)}
                color="primary"
                title="View JSON Profile"
                sx={{
                  height: 'fit-content',
                }}
              >
                <CodeIcon />
              </IconButton>
              <IconButton 
                onClick={handleDelete}
                color="error"
                sx={{
                  height: 'fit-content',
                  '&:hover': {
                    backgroundColor: 'error.light',
                  }
                }}
              >
                <DeleteIcon />
              </IconButton>

              {/* Map */}
              {mapCenter && (
                <Box sx={{ height: 300, width: 300, flexShrink: 0 }}>
                  <MapComponent
                    mapCenter={mapCenter}
                    polygonPath={[]}
                    cornerMarkers={{}}
                    locationName={user.displayName}
                    neighborhoodBoundaries={[]}
                    centerPoint={mapCenter}
                    isPlace={true}
                    height={300}
                    width={300}
                    zoom={15}
                  />
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <EditableInfoRow 
                label="Role"
                value={user.isAdmin ? 'Admin' : (user.role || 'User')}
                isEditing={editingField === 'role'}
                onEdit={() => setEditingField('role')}
                onSave={(value) => {
                  // Convert display values to backend values
                  const roleMap: { [key: string]: string } = {
                    'User': 'user',
                    'Admin': 'admin',
                    'Moderator': 'moderator'
                  };
                  const backendValue = roleMap[value] || value.toLowerCase();
                  handleSave('role', backendValue);
                }}
                onCancel={() => setEditingField(null)}
                options={['User', 'Admin', 'Moderator']}
              />
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                py: 0.5,
              }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ minWidth: 120, fontWeight: 500 }}
                >
                  Status:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Box component="span" sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: user.isOnline ? 'success.main' : 'text.secondary',
                    flex: 1
                  }}>
                    {user.isOnline ? '🟢 Online' : '⚫ Offline'}
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={() => {
                      const newOnlineStatus = !user.isOnline;
                      handleSave('isOnline', newOnlineStatus);
                    }}
                    title={`Toggle user ${user.isOnline ? 'offline' : 'online'}`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                py: 0.5,
              }}>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ minWidth: 120, fontWeight: 500 }}
                >
                  Multiple Promotes:
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                  <Box component="span" sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    flex: 1
                  }}>
                    {allowMultiplePromotes ? 'Enabled' : 'Disabled'}
                  </Box>
                  <IconButton 
                    size="small" 
                    onClick={handleMultiplePromotesClick}
                    title={`${allowMultiplePromotes ? 'Disable' : 'Enable'} multiple promotes`}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              <EditableInfoRow 
                label="Display Name"
                value={user.displayName}
                isEditing={editingField === 'displayName'}
                onEdit={() => setEditingField('displayName')}
                onSave={(value) => handleSave('displayName', value)}
                onCancel={() => setEditingField(null)}
              />

              <EditableInfoRow 
                label="Username"
                value={user.username}
                isEditing={editingField === 'username'}
                onEdit={() => setEditingField('username')}
                onSave={(value) => handleSave('username', value)}
                onCancel={() => setEditingField(null)}
              />

              <EditableInfoRow 
                label="Birthday"
                value={
                  // Handle both computed and raw birthday formats
                  user.computedBirthday?.date 
                    ? new Date(user.computedBirthday.date).toLocaleDateString()
                    : user.birthday 
                      ? new Date(user.birthday).toLocaleDateString()
                      : undefined
                }
                isEditing={editingField === 'birthday'}
                onEdit={() => setEditingField('birthday')}
                onSave={(value) => handleSave('birthday', value)}
                onCancel={() => setEditingField(null)}
                isPrivate={user.computedBirthday?.isPrivate}
                defaultValue={
                  user.computedBirthday?.date 
                    ? new Date(user.computedBirthday.date).toISOString().slice(0, 10)
                    : user.birthday 
                      ? new Date(user.birthday).toISOString().slice(0, 10)
                      : ''
                }
                type="date"
              />
              {identityOptions && (
                <EditableInfoRow 
                  label="Gender"
                  value={
                    // Handle both computed and raw gender formats
                    user.computedGender?.content
                      ? identityOptions.gender.find(opt => opt.id === user.computedGender?.content)?.value || user.computedGender.content
                      : typeof user.gender === 'string'
                        ? identityOptions.gender.find(opt => opt.id === user.gender)?.value || user.gender
                        : undefined
                  }
                  isEditing={editingField === 'gender'}
                  onEdit={() => setEditingField('gender')}
                  onSave={(value) => {
                    const option = identityOptions.gender.find(opt => opt.value === value);
                    if (option) {
                      handleSave('gender', option.id);
                      setEditingField(null);
                    }
                  }}
                  onCancel={() => setEditingField(null)}
                  options={identityOptions.gender.map(opt => opt.value)}
                  isPrivate={user.computedGender?.isPrivate}
                />
              )}
              {identityOptions && (
                <EditableInfoRow 
                  label="Race"
                  value={
                    // Handle both computed and raw race formats
                    user.computedRace?.content
                      ? identityOptions.race.find(opt => opt.id === user.computedRace?.content)?.value || user.computedRace.content
                      : typeof user.race === 'string'
                        ? identityOptions.race.find(opt => opt.id === user.race)?.value || user.race
                        : undefined
                  }
                  isEditing={editingField === 'race'}
                  onEdit={() => setEditingField('race')}
                  onSave={(value) => {
                    const option = identityOptions.race.find(opt => opt.value === value);
                    if (option) {
                      handleSave('race', option.id);
                      setEditingField(null);
                    }
                  }}
                  onCancel={() => setEditingField(null)}
                  options={identityOptions.race.map(opt => opt.value)}
                  isPrivate={user.computedRace?.isPrivate}
                />
              )}
              {identityOptions && (
                <EditableInfoRow 
                  label="Orientation"
                  value={
                    // Handle both computed and raw sexual orientation formats
                    user.computedSexualOrientation?.content
                      ? identityOptions.sexualOrientation.find(opt => opt.id === user.computedSexualOrientation?.content)?.value || user.computedSexualOrientation.content
                      : typeof user.sexualOrientation === 'string'
                        ? identityOptions.sexualOrientation.find(opt => opt.id === user.sexualOrientation)?.value || user.sexualOrientation
                        : undefined
                  }
                  isEditing={editingField === 'sexualOrientation'}
                  onEdit={() => setEditingField('sexualOrientation')}
                  onSave={(value) => {
                    const option = identityOptions.sexualOrientation.find(opt => opt.value === value);
                    if (option) {
                      handleSave('sexualOrientation', option.id);
                      setEditingField(null);
                    }
                  }}
                  onCancel={() => setEditingField(null)}
                  options={identityOptions.sexualOrientation.map(opt => opt.value)}
                  isPrivate={user.computedSexualOrientation?.isPrivate}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {user.currentNeighborhood && (
                <EditableInfoRow 
                  label="Neighborhood"
                  value={getLocationName(user.currentNeighborhood)}
                  isEditing={editingField === 'neighborhood'}
                  onEdit={() => setEditingField('neighborhood')}
                  onSave={(value) => handleSave('currentNeighborhood', value)}
                  onCancel={() => setEditingField(null)}
                />
              )}
              {user.currentCity && (
                <EditableInfoRow 
                  label="City"
                  value={getLocationName(user.currentCity)}
                  isEditing={editingField === 'city'}
                  onEdit={() => setEditingField('city')}
                  onSave={(value) => handleSave('currentCity', value)}
                  onCancel={() => setEditingField(null)}
                />
              )}
              {user.currentState && (
                <EditableInfoRow 
                  label="State"
                  value={getLocationName(user.currentState)}
                  isEditing={editingField === 'state'}
                  onEdit={() => setEditingField('state')}
                  onSave={(value) => handleSave('currentState', value)}
                  onCancel={() => setEditingField(null)}
                />
              )}
              {user.currentCountry && (
                <EditableInfoRow 
                  label="Country"
                  value={getLocationName(user.currentCountry)}
                  isEditing={editingField === 'country'}
                  onEdit={() => setEditingField('country')}
                  onSave={(value) => handleSave('currentCountry', value)}
                  onCancel={() => setEditingField(null)}
                />
              )}
              {hasLocation && user.location && (
                <>
                  <EditableInfoRow 
                    label="Latitude"
                    value={user.location.coordinates[1].toFixed(6)}
                    isEditing={editingField === 'latitude'}
                    onEdit={() => setEditingField('latitude')}
                    onSave={(value) => handleSave('latitude', value)}
                    onCancel={() => setEditingField(null)}
                  />
                  <EditableInfoRow 
                    label="Longitude"
                    value={user.location.coordinates[0].toFixed(6)}
                    isEditing={editingField === 'longitude'}
                    onEdit={() => setEditingField('longitude')}
                    onSave={(value) => handleSave('longitude', value)}
                    onCancel={() => setEditingField(null)}
                  />
                </>
              )}
              {user.bio && (
                <EditableInfoRow 
                  label="Bio"
                  value={user.bio}
                  isEditing={editingField === 'bio'}
                  onEdit={() => setEditingField('bio')}
                  onSave={(value) => handleSave('bio', value)}
                  onCancel={() => setEditingField(null)}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Privacy Overview */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            {user.privacy ? (
              <PrivacyOverview
                privacy={user.privacy}
                onUpdate={(updatedPrivacy) => handleSave('privacy', updatedPrivacy)}
              />
            ) : (
              <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.contrastText">
                  This user is using the legacy privacy system. Privacy data not available for editing.
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {/* JSON Modal */}
        <Dialog
          open={jsonModalOpen}
          onClose={() => setJsonModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            User Profile JSON
            <IconButton
              aria-label="close"
              onClick={() => setJsonModalOpen(false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#f5f5f5',
                padding: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: '70vh',
                '& code': {
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }
              }}
            >
              <code>
                {JSON.stringify(user, null, 2)}
              </code>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJsonModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default UserCard; 